"use client";

import { ReactNode, useEffect, useState, useRef, lazy, Suspense } from "react";
import Link from "next/link";
import Image from "next/image";
import { User } from "firebase/auth";
import { auth } from "../lib/firebase";
import { usePathname, useRouter } from "next/navigation";
import UserProfileMenu from "./UserProfileMenu";
import "../lib/bugsnag"; // Initialize Bugsnag
import { BugsnagErrorBoundary, setBugsnagUser } from "../lib/bugsnag";
import { initHotjar } from "../lib/hotjar";
import MetaPixel from "./MetaPixel";
import GoogleAnalytics from "./GoogleAnalytics";
import { userLogger } from "../lib/userLogger";
import { ErrorModalProvider } from "@/contexts/ErrorModalContext";
import {
  Menu,
  X,
  Calendar,
  Ticket,
  FileText,
  Globe,
  Users,
  Settings,
  ClipboardList,
  Music
} from "lucide-react";
import { useUser } from "../hooks/useUser";
import { useHasFestivals } from "../hooks/useHasFestivals";

// Venue manager pages - show venue nav items in mobile menu.
//
// Every destination in the mobile menu's venue-manager section must be listed
// here, or the section disappears on the page it links to — leaving that page
// with no mobile navigation at all. /passes and /festivals were both missing.
const venueManagerPaths = [
  "/calendar",
  "/events",
  "/passes",
  "/festivals",
  "/booking",
  "/templates",
  "/sitekit",
  "/staff",
  "/settings",
  "/offer",
];

// Artist manager pages - show artist nav items in mobile menu
const artistManagerPaths = [
  "/artists",
  "/artist",
];

// Lazy load the heavy context providers - only load when user is authenticated
const AuthenticatedProviders = lazy(() => import("./AuthenticatedProviders"));

interface ClientLayoutProps {
  children: ReactNode;
}

export default function ClientLayout({ children }: ClientLayoutProps) {
  const [user, setUser] = useState<User | null>(null);
  const [burgerToggle, setBurgerToggle] = useState(false);
  // Whether to offer FESTIVALS in the mobile menu. Called here with the other
  // hooks because this component returns early while auth resolves, and a hook
  // cannot run conditionally. The desktop sidebar reads organizerEntities from
  // ApplicationContext instead; the mobile menu renders above that provider.
  const hasFestivals = useHasFestivals(user?.uid);
  const [isClient, setIsClient] = useState(false);
  const [authResolved, setAuthResolved] = useState(false);

  // Get Firestore user data for role flags
  const { user: firestoreUser } = useUser();

  const router = useRouter();
  const pathname = usePathname();

  const handleLinkClick = () => {
    setBurgerToggle(false);
  };

  // Initialize client-side rendering - ALWAYS call this
  useEffect(() => {
    setIsClient(true);
    // Initialize Hotjar for production only
    initHotjar();
  }, []);

  // Helper to check if a path is a public page
  const checkIsPublicPage = (path: string | null) => {
    return path === "/login" ||
      path === "/" ||
      path === "/verify-email" ||
      path === "/auth/action" ||
      // Someone resetting a forgotten password is by definition signed out.
      path === "/reset-password" ||
      path === "/support" ||
      path?.startsWith("/invitation/") ||
      path?.startsWith("/contracts/") ||
      path?.startsWith("/artistProfileEditor/") ||
      // Artists manage sales-update preferences from an emailed link, and are
      // never required to have an account to do it.
      path === "/preferences/artist" ||
      // Dev-only component harnesses (e.g. /dev/image-editor). Guarded to
      // non-production; those routes also 404 in production server-side.
      (process.env.NODE_ENV !== "production" && !!path?.startsWith("/dev/"));
  };

  const isPublicPage = checkIsPublicPage(pathname);

  // Store the current Firebase user in a ref so we can access it when pathname changes
  const firebaseUserRef = useRef<User | null>(null);
  // Track the current pathname in a ref so auth listener has access to latest value
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;

  // Auth listener - set up once to track Firebase auth state
  // Sets both the ref AND the user state atomically to avoid render gaps
  useEffect(() => {
    if (!isClient) return;

    const authListener = auth.onAuthStateChanged((authUser) => {
      firebaseUserRef.current = authUser;

      // Use the ref to get the current pathname (not stale closure value)
      const currentPath = pathnameRef.current;
      const currentIsPublic = checkIsPublicPage(currentPath);
      const isRealUser = authUser && !authUser.isAnonymous;

      if (currentIsPublic) {
        // On public pages, don't show authenticated UI
        setUser(null);
        setBugsnagUser(null);
      } else if (isRealUser) {
        // On protected pages with authenticated user, show UI
        setUser(authUser);
        setBugsnagUser(authUser.uid, authUser.email, authUser.displayName);
        console.log("User authenticated:", authUser.uid);
      } else {
        // On protected pages without auth, redirect to login
        setUser(null);
        setBugsnagUser(null);
        router.push("/login");
      }

      setAuthResolved(true);
    });
    return () => authListener();
  }, [isClient, router]);

  // Handle pathname changes AFTER initial auth is resolved
  // This handles navigation between pages (e.g., login -> events)
  useEffect(() => {
    if (!isClient || !authResolved) return;

    const firebaseUser = firebaseUserRef.current;
    const isRealUser = firebaseUser && !firebaseUser.isAnonymous;

    if (isPublicPage) {
      // On public pages, hide authenticated UI
      setUser(null);
    } else if (isRealUser) {
      // On protected pages with authenticated non-anonymous user, show UI
      setUser(firebaseUser);
    } else {
      // On protected pages without auth or with anonymous user, redirect to login
      setUser(null);
      router.push("/login");
    }
  }, [pathname, isClient, authResolved, isPublicPage, router]);

  // Handle window resize - ALWAYS call this
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setBurgerToggle(false);
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Disable scrolling when burger open - ALWAYS call this
  useEffect(() => {
    if (burgerToggle) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
      document.documentElement.style.overflow = "auto";
    }
  }, [burgerToggle]);

  const handleLogout = async () => {
    try {
      // Log the logout activity before signing out (while we still have auth)
      await userLogger.logLogout();
      await auth.signOut();
      setUser(null);
      router.push("/login");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  // Show loading state until auth is resolved, OR if we're on a protected page
  // without a user (redirect is pending). This prevents children from rendering
  // without ApplicationProvider during logout or when navigating to protected
  // pages before auth is confirmed.
  const needsAuth = !isPublicPage && !user;
  if (!isClient || !authResolved || needsAuth) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontFamily: "var(--font-rubik)",
          fontSize: "18px",
        }}
      >
        Loading...
      </div>
    );
  }

  // Determine which manager section we're in
  const isVenueManagerPage = venueManagerPaths.some(path => pathname?.startsWith(path));
  const isArtistManagerPage = artistManagerPaths.some(path => pathname?.startsWith(path));

  // Hide "My Artists" and "Booking" for artist-only users (primaryRole is 'artist')
  const isArtistOnly = firestoreUser?.primaryRole === 'artist';

  // Helper to check if a nav item is active
  const isActive = (path: string) => pathname === path || pathname?.startsWith(path + "/");

  return (
    <ErrorModalProvider>
      {/* Meta Pixel — base script + PageView on route changes. No-op without
          NEXT_PUBLIC_META_PIXEL_ID. Wrapped in Suspense for useSearchParams. */}
      <Suspense fallback={null}>
        <MetaPixel />
        {/* GA4 — gtag.js + one page_view per route change, production only.
            Reports into the same stream as www.gobo.show. Suspense is required
            for useSearchParams. */}
        <GoogleAnalytics />
      </Suspense>
      {/* Header Links for all pages */}
      <header
        className={user ? "bg-white shadow-md" : "bg-black shadow-md"}
      >
        <nav className="flex justify-between items-center w-full px-4">
          {/* Logo */}
          <div className={`ml-6 my-5 ${burgerToggle ? "hidden" : ""}`}>
            <Link href={user ? "/events" : "/"}>
              <Image
                src={
                  user
                    ? "/img/gobo-logo-black.svg"
                    : "/img/gobo-logo-white.svg"
                }
                alt="Gobo Logo"
                width={100}
                height={40}
              />
            </Link>
          </div>

          {/* Mobile menu button - visible below lg */}
          {user && (
            <button
              aria-label="toggle menu"
              onClick={() => setBurgerToggle(!burgerToggle)}
              className="lg:hidden text-black text-2xl absolute top-5 right-4 p-2 z-50"
            >
              {burgerToggle ? <X size={28} /> : <Menu size={28} />}
            </button>
          )}

          {/* Desktop Navigation - visible at lg+ */}
          <div className="hidden lg:flex items-center gap-4 uppercase">
            {user ? (
              <>
                <Link
                  href="/events"
                  className={`px-3 py-2 text-gray-700 hover:text-customTeal ${
                    isActive("/events") ? "border-b-2 border-yellow-500" : ""
                  }`}
                >
                  My Events
                </Link>
                {!isArtistOnly && (
                  <Link
                    href="/artists"
                    className={`px-3 py-2 text-gray-700 hover:text-customTeal ${
                      isActive("/artists") ? "border-b-2 border-yellow-500" : ""
                    }`}
                  >
                    My Artists
                  </Link>
                )}
                <Link
                  href="/audience"
                  className={`px-3 py-2 text-gray-700 hover:text-customTeal ${
                    isActive("/audience") ? "border-b-2 border-yellow-500" : ""
                  }`}
                >
                  My Audience
                </Link>
                <Link
                  href="/help"
                  className={`px-3 py-2 text-gray-700 hover:text-customTeal ${
                    isActive("/help") ? "border-b-2 border-yellow-500" : ""
                  }`}
                >
                  Help
                </Link>
                <div className="pl-2">
                  <UserProfileMenu
                    user={user}
                    avatarUrl={
                      // useUser defaults avatar to a placeholder path; treat
                      // that (and empty) as "no image" so the initial shows.
                      firestoreUser?.avatar && firestoreUser.avatar !== "/img/icons/icon-profile.svg"
                        ? firestoreUser.avatar
                        : undefined
                    }
                    name={firestoreUser?.name}
                    onSignOut={handleLogout}
                  />
                </div>
              </>
            ) : (
              <> 
                <Link href="https://www.gobo.show/why-gobo/" className="px-3 py-3 text-white">
                  Why Gobo?
                </Link>
                <Link href="https://www.gobo.show/who-were-for/" className="px-3 py-3 text-white">
                  Who We&apos;re For
                </Link>
                <Link href="https://www.gobo.show/compare/" className="px-3 py-3 text-white">
                  Compare?
                </Link>
                <Link href="https://www.gobo.show/pricing/" className="px-3 py-3 text-white">
                  Pricing
                </Link>
                <Link href="https://tixlist.gobo.show" className="px-3 py-3 text-white">
                  Buy Tickets
                </Link>
                <Link
                  href="/login"
                  className="px-3 py-3 text-white bg-customGray rounded-md uppercase hover:bg-gray-500 hover:text-white"
                >
                  Sign up or Login
                </Link>
              </>
            )}
          </div>

        </nav>
      </header>

      {/* Mobile Menu Overlay */}
      {burgerToggle && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setBurgerToggle(false)}
        />
      )}

      {/* Mobile Menu Panel */}
      {burgerToggle && (
        <div className="fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-[#2c2c2c] z-50 overflow-y-auto">
          {/* Close button */}
          <button
            aria-label="close menu"
            onClick={() => setBurgerToggle(false)}
            className="absolute top-5 right-4 p-2 text-white"
          >
            <X size={28} />
          </button>
          <div className="p-6 pt-16">
              {/* Venue Manager Nav Items */}
              {user && isVenueManagerPage && (
                <div className="mb-6">
                  <h2 className="text-white font-black text-2xl uppercase tracking-wide mb-4">
                    VENUE MANAGER
                  </h2>
                  <div className="space-y-1">
                    <Link
                      href="/calendar"
                      onClick={handleLinkClick}
                      className={`flex items-center gap-3 px-4 py-3 rounded-md text-white font-medium ${
                        isActive("/calendar") ? "bg-[#3e3e3e]" : "hover:bg-[#3e3e3e]"
                      }`}
                    >
                      <Calendar size={20} />
                      <span>CALENDAR</span>
                    </Link>
                    <Link
                      href="/events"
                      onClick={handleLinkClick}
                      className={`flex items-center gap-3 px-4 py-3 rounded-md text-white font-medium ${
                        isActive("/events") ? "bg-[#3e3e3e]" : "hover:bg-[#3e3e3e]"
                      }`}
                    >
                      <Ticket size={20} />
                      <span>MY EVENTS</span>
                    </Link>
                    <Link
                      href="/passes"
                      onClick={handleLinkClick}
                      className={`flex items-center gap-3 px-4 py-3 rounded-md text-white font-medium ${
                        isActive("/passes") ? "bg-[#3e3e3e]" : "hover:bg-[#3e3e3e]"
                      }`}
                    >
                      <Ticket size={20} />
                      <span>PASSES</span>
                    </Link>
                    {/* Order and gating match the desktop sidebar
                        (VenueManagerNav): Festivals sits under Passes and is
                        shown only to people who actually manage a festival. */}
                    {hasFestivals && (
                      <Link
                        href="/festivals"
                        onClick={handleLinkClick}
                        className={`flex items-center gap-3 px-4 py-3 rounded-md text-white font-medium ${
                          isActive("/festivals") ? "bg-[#3e3e3e]" : "hover:bg-[#3e3e3e]"
                        }`}
                      >
                        <Ticket size={20} />
                        <span>FESTIVALS</span>
                      </Link>
                    )}
                    {!isArtistOnly && (
                      <Link
                        href="/booking"
                        onClick={handleLinkClick}
                        className={`flex items-center gap-3 px-4 py-3 rounded-md text-white font-medium ${
                          isActive("/booking") ? "bg-[#3e3e3e]" : "hover:bg-[#3e3e3e]"
                        }`}
                      >
                        <ClipboardList size={20} />
                        <span>BOOKING</span>
                      </Link>
                    )}
                    <Link
                      href="/templates"
                      onClick={handleLinkClick}
                      className={`flex items-center gap-3 px-4 py-3 rounded-md text-white font-medium ${
                        isActive("/templates") ? "bg-[#3e3e3e]" : "hover:bg-[#3e3e3e]"
                      }`}
                    >
                      <FileText size={20} />
                      <span>TEMPLATES</span>
                    </Link>
                    <Link
                      href="/sitekit"
                      onClick={handleLinkClick}
                      className={`flex items-center gap-3 px-4 py-3 rounded-md text-white font-medium ${
                        isActive("/sitekit") ? "bg-[#3e3e3e]" : "hover:bg-[#3e3e3e]"
                      }`}
                    >
                      <Globe size={20} />
                      <span>SITE KIT</span>
                    </Link>
                    <Link
                      href="/staff"
                      onClick={handleLinkClick}
                      className={`flex items-center gap-3 px-4 py-3 rounded-md text-white font-medium ${
                        isActive("/staff") ? "bg-[#3e3e3e]" : "hover:bg-[#3e3e3e]"
                      }`}
                    >
                      <Users size={20} />
                      <span>STAFF</span>
                    </Link>
                    <Link
                      href="/settings"
                      onClick={handleLinkClick}
                      className={`flex items-center gap-3 px-4 py-3 rounded-md text-white font-medium ${
                        isActive("/settings") ? "bg-[#3e3e3e]" : "hover:bg-[#3e3e3e]"
                      }`}
                    >
                      <Settings size={20} />
                      <span>SETTINGS</span>
                    </Link>
                  </div>
                  <div className="border-t border-gray-600 my-4" />
                </div>
              )}

              {/* Artist Manager Nav Items */}
              {user && isArtistManagerPage && (
                <div className="mb-6">
                  <h2 className="text-white font-black text-2xl uppercase tracking-wide mb-4">
                    ARTIST MANAGER
                  </h2>
                  <div className="space-y-1">
                    <Link
                      href="/artists"
                      onClick={handleLinkClick}
                      className={`flex items-center gap-3 px-4 py-3 rounded-md text-white font-medium ${
                        pathname === "/artists" ? "bg-[#3e3e3e]" : "hover:bg-[#3e3e3e]"
                      }`}
                    >
                      <Music size={20} />
                      <span>ARTISTS</span>
                    </Link>
                    <Link
                      href="/artists/settings"
                      onClick={handleLinkClick}
                      className={`flex items-center gap-3 px-4 py-3 rounded-md text-white font-medium ${
                        isActive("/artists/settings") ? "bg-[#3e3e3e]" : "hover:bg-[#3e3e3e]"
                      }`}
                    >
                      <Settings size={20} />
                      <span>SETTINGS</span>
                    </Link>
                  </div>
                  <div className="border-t border-gray-600 my-4" />
                </div>
              )}

              {/* Common Navigation Items */}
              {user && (
                <div className="space-y-1">
                  <Link
                    href="/events"
                    onClick={handleLinkClick}
                    className="block px-4 py-3 text-white hover:bg-[#3e3e3e] rounded-md"
                  >
                    My Events
                  </Link>
                  {!isArtistOnly && (
                    <Link
                      href="/artists"
                      onClick={handleLinkClick}
                      className="block px-4 py-3 text-white hover:bg-[#3e3e3e] rounded-md"
                    >
                      My Artists
                    </Link>
                  )}
                  <Link
                    href="/audience"
                    onClick={handleLinkClick}
                    className="block px-4 py-3 text-white hover:bg-[#3e3e3e] rounded-md"
                  >
                    My Audience
                  </Link>
                  <Link
                    href="/help"
                    onClick={handleLinkClick}
                    className="block px-4 py-3 text-white hover:bg-[#3e3e3e] rounded-md"
                  >
                    Help
                  </Link>
                  <Link
                    href="/profile"
                    onClick={handleLinkClick}
                    className="block px-4 py-3 text-white hover:bg-[#3e3e3e] rounded-md"
                  >
                    Account Settings
                  </Link>
                  <button
                    onClick={() => {
                      handleLinkClick();
                      handleLogout();
                    }}
                    className="block w-full text-left px-4 py-3 text-white hover:bg-[#3e3e3e] rounded-md"
                  >
                    Sign Out
                  </button>
                </div>
              )}

              {/* Non-authenticated menu items */}
              {!user && (
                <div className="space-y-1">
                  <Link
                    href="/"
                    onClick={handleLinkClick}
                    className="block px-4 py-3 text-white hover:bg-[#3e3e3e] rounded-md"
                  >
                    Buy Tickets
                  </Link>
                  <Link
                    href="https://www.gobo.show/why-gobo/"
                    onClick={handleLinkClick}
                    className="block px-4 py-3 text-white hover:bg-[#3e3e3e] rounded-md"
                  >
                    Why Gobo?
                  </Link>
                  <Link
                    href="/login"
                    onClick={handleLinkClick}
                    className="block px-4 py-3 mt-4 bg-gray-600 text-white rounded-md text-center uppercase hover:bg-gray-500"
                  >
                    Sign up or Login
                  </Link>
                </div>
              )}
          </div>
        </div>
      )}

      <main className="flex-grow">
        <BugsnagErrorBoundary>
          {/* Only wrap with heavy context providers when user is authenticated */}
          {user ? (
            <Suspense fallback={<div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>Loading...</div>}>
              <AuthenticatedProviders>
                {children}
              </AuthenticatedProviders>
            </Suspense>
          ) : (
            children
          )}
        </BugsnagErrorBoundary>
      </main>
    </ErrorModalProvider>
  );
}
